import { useTranslation } from 'react-i18next';
import { Plus, Search, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export type GridTab = 'all' | 'active' | 'scheduled' | 'expired' | 'draft';

export interface GridCounts {
	total: number;
	active: number;
	scheduled: number;
	expired: number;
	draft: number;
}

interface GridChromeProps {
	counts: GridCounts;
	search: string;
	onSearch: (value: string) => void;
	activeTab: GridTab;
	onTabChange: (tab: GridTab) => void;
	onNew: () => void;
	onOpenFilters: () => void;
}

export function GridChrome({ counts, search, onSearch, activeTab, onTabChange, onNew, onOpenFilters }: GridChromeProps) {
	const { t } = useTranslation();

	const tabs: { key: GridTab; label: string; count: number }[] = [
		{ key: 'all', label: t('promotion.tabs.all'), count: counts.total },
		{ key: 'active', label: t('promotion.tabs.active'), count: counts.active },
		{ key: 'scheduled', label: t('promotion.tabs.scheduled'), count: counts.scheduled },
		{ key: 'expired', label: t('promotion.tabs.expired'), count: counts.expired },
		{ key: 'draft', label: t('promotion.tabs.draft'), count: counts.draft }
	];

	const subtitle = `${counts.total} πολιτικές · ${counts.active} ενεργές · ${counts.scheduled} προγραμματισμένες · ${counts.draft} πρόχειρες`;

	return (
		<div className="rounded-lg border border-border bg-surface p-4 px-5">
			{/* No in-page breadcrumb: the topbar already renders the menu-derived trail */}
			<div className="mb-3 flex items-start justify-between gap-4">
				<div>
					<h1 className="text-2xl font-semibold tracking-[-0.015em] text-foreground">{t('promotion.surface.grid.title')}</h1>
					<p className="mt-0.5 text-sm text-foreground-muted">{subtitle}</p>
				</div>
				<div className="flex items-center gap-2">
					<div className="relative min-w-60">
						<Search size={16} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-subtle" />
						<Input value={search} onChange={e => onSearch(e.target.value)} placeholder={t('common.search')} className="pl-8" />
					</div>
					<Button variant="outline" onClick={onOpenFilters}>
						<SlidersHorizontal size={16} /> {t('common.filters')}
					</Button>
					<Button onClick={onNew}>
						<Plus size={16} /> {t('promotion.surface.grid.new')}
					</Button>
				</div>
			</div>

			<div className="mt-3 flex gap-1 border-b border-border-subtle">
				{tabs.map(tab => {
					const active = tab.key === activeTab;
					return (
						<button
							key={tab.key}
							type="button"
							onClick={() => onTabChange(tab.key)}
							className={cn(
								'-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition-colors',
								active ? 'border-brand text-brand' : 'border-transparent text-foreground-muted hover:text-foreground'
							)}>
							{tab.label}
							<span
								className={cn(
									'rounded-pill px-1.5 text-[10px]',
									active ? 'bg-brand-subtle text-brand-subtle-fg' : 'bg-muted text-foreground-subtle'
								)}>
								{tab.count}
							</span>
						</button>
					);
				})}
			</div>
		</div>
	);
}
